import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, ListChecks } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const KIND_LABELS = {
  task: "Task",
  delegation: "Delegation",
  follow_up: "Follow-up",
  approval_request: "Approval Request",
};

const STATUS_STYLES = {
  new: "bg-elevated text-textmuted border-seam",
  ready_to_delegate: "bg-champagne/10 text-champagne border-champagne/30",
  assigned: "bg-champagne/15 text-champagne border-champagne/40",
  accepted: "bg-champagne/15 text-champagne border-champagne/40",
  in_progress: "bg-champagne/15 text-champagne border-champagne/40",
  waiting: "bg-elevated text-textmuted border-seam",
  needs_approval: "bg-[#7a4a30]/20 text-[#E5B493] border-[#7a4a30]/50",
  completed: "bg-[#4D5B46]/30 text-[#A3C293] border-[#4D5B46]/50",
  overdue: "bg-[#5C2B29]/30 text-[#E5A09E] border-[#5C2B29]/50",
  escalated: "bg-[#5C2B29]/30 text-[#E5A09E] border-[#5C2B29]/50",
  cancelled: "bg-elevated text-textmuted border-seam",
};

const PRIORITY_STYLES = {
  low: "text-textmuted",
  medium: "text-champagne",
  high: "text-[#E5B493]",
  critical: "text-[#E5A09E]",
};

const NewTaskDialog = ({ open, onOpenChange, onCreated, meta, entities, admins }) => {
  const [form, setForm] = useState({
    kind: "task",
    title: "",
    instruction: "",
    expected_result: "",
    priority: "medium",
    deadline: "",
    follow_up_date: "",
    approval_required: false,
    entity_id: "",
    assigned_to: "",
    status: "new",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        kind: "task",
        title: "",
        instruction: "",
        expected_result: "",
        priority: "medium",
        deadline: "",
        follow_up_date: "",
        approval_required: false,
        entity_id: "",
        assigned_to: "",
        status: "new",
      });
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.entity_id) delete payload.entity_id;
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.deadline) delete payload.deadline;
      if (!payload.follow_up_date) delete payload.follow_up_date;
      if (payload.kind === "approval_request") payload.approval_required = true;
      const { data } = await api.post("/enterprise/tasks", payload);
      toast.success("Created.");
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const kinds = meta?.task_kinds || ["task", "delegation", "follow_up", "approval_request"];
  const statuses = meta?.task_statuses || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-task-dialog"
        className="bg-surface border-seam max-w-lg text-textmain max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl">New</DialogTitle>
          <DialogDescription className="text-textmuted">
            Create a task, delegation, follow-up or approval request.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="block label-eyebrow mb-2">Kind</label>
            <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
              <SelectTrigger data-testid="task-kind-select" className="bg-obsidian border-seam text-textmain">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-seam text-textmain">
                {kinds.map((k) => (
                  <SelectItem key={k} value={k}>{KIND_LABELS[k] || k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block label-eyebrow mb-2">Title</label>
            <input
              required
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              data-testid="task-title-input"
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
              placeholder="What must be done?"
            />
          </div>

          <div>
            <label className="block label-eyebrow mb-2">Instruction</label>
            <textarea
              rows={3}
              value={form.instruction}
              onChange={(e) => set("instruction", e.target.value)}
              data-testid="task-instruction-input"
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 resize-none"
              placeholder="Specific direction for the assignee…"
            />
          </div>

          <div>
            <label className="block label-eyebrow mb-2">Expected result</label>
            <textarea
              rows={2}
              value={form.expected_result}
              onChange={(e) => set("expected_result", e.target.value)}
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 resize-none"
              placeholder="How will 'done' be judged?"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block label-eyebrow mb-2">Priority</label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="bg-obsidian border-seam text-textmain">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-seam text-textmain">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block label-eyebrow mb-2">Status</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="bg-obsidian border-seam text-textmain">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-seam text-textmain max-h-64">
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block label-eyebrow mb-2">Deadline</label>
              <input
                type="date"
                value={form.deadline}
                onChange={(e) => set("deadline", e.target.value)}
                className="w-full bg-obsidian border border-seam rounded-md px-3 py-2.5 focus:outline-none focus:border-champagne text-textmain [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="block label-eyebrow mb-2">Follow-up</label>
              <input
                type="date"
                value={form.follow_up_date}
                onChange={(e) => set("follow_up_date", e.target.value)}
                className="w-full bg-obsidian border border-seam rounded-md px-3 py-2.5 focus:outline-none focus:border-champagne text-textmain [color-scheme:dark]"
              />
            </div>
          </div>

          <div>
            <label className="block label-eyebrow mb-2">Entity (optional)</label>
            <select
              value={form.entity_id}
              onChange={(e) => set("entity_id", e.target.value)}
              className="w-full bg-obsidian border border-seam rounded-md px-3 py-2.5 focus:outline-none focus:border-champagne text-textmain"
            >
              <option value="">— None</option>
              {entities.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block label-eyebrow mb-2">Assign to (optional)</label>
            <select
              value={form.assigned_to}
              onChange={(e) => set("assigned_to", e.target.value)}
              data-testid="task-assign-select"
              className="w-full bg-obsidian border border-seam rounded-md px-3 py-2.5 focus:outline-none focus:border-champagne text-textmain"
            >
              <option value="">— Unassigned</option>
              {admins
                .filter((a) => a.user_id && a.status === "active")
                .map((a) => (
                  <option key={a.id} value={a.user_id}>
                    {a.name || a.email} · {a.role.replace(/_/g, " ")}
                  </option>
                ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-textmuted cursor-pointer">
            <input
              type="checkbox"
              checked={form.approval_required}
              onChange={(e) => set("approval_required", e.target.checked)}
              className="w-4 h-4 accent-champagne"
            />
            Requires CEO approval
          </label>

          <div className="flex justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-5 py-2.5 rounded-md border border-seam text-textmuted hover:text-textmain hover:border-champagne/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="task-submit-btn"
              className="px-6 py-2.5 rounded-md bg-champagne text-obsidian font-medium hover:bg-champagne-soft transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)] disabled:opacity-60 inline-flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Create
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EnterpriseTasks = () => {
  const { admin, hasPermission } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [entities, setEntities] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterKind, setFilterKind] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const canCreate = hasPermission("tasks.create");
  const canEdit = hasPermission("tasks.edit");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterKind) params.kind = filterKind;
      if (filterStatus) params.status = filterStatus;
      const [t, e, m, a] = await Promise.all([
        api.get("/enterprise/tasks", { params }),
        api.get("/enterprise/entities"),
        api.get("/enterprise/meta"),
        api.get("/enterprise/admins").catch(() => ({ data: [] })),
      ]);
      setTasks(t.data);
      setEntities(e.data);
      setMeta(m.data);
      setAdmins(a.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load tasks");
    } finally {
      setLoading(false);
    }
  }, [filterKind, filterStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const entityById = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e])), [entities]);
  const adminByUserId = useMemo(
    () => Object.fromEntries(admins.filter((a) => a.user_id).map((a) => [a.user_id, a])),
    [admins],
  );

  const updateStatus = async (task, newStatus) => {
    try {
      const { data } = await api.patch(`/enterprise/tasks/${task.id}`, { status: newStatus });
      setTasks((prev) => prev.map((x) => (x.id === task.id ? data : x)));
      toast.success("Updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  return (
    <Layout>
      <section className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div className="max-w-2xl">
            <p className="label-eyebrow">Executive delegation</p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">
              Tasks & Delegations
            </h1>
            <p className="text-textmuted mt-3 leading-relaxed">
              Every task, delegation, follow-up and approval request across the enterprise.
              Collaborate with your Executive Assistant — one system of record.
            </p>
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              data-testid="new-task-btn"
              className="inline-flex items-center gap-2 self-start bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-5 py-2.5 transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)]"
            >
              <Plus className="w-4 h-4" strokeWidth={1.7} />
              New
            </button>
          )}
        </div>
      </section>

      <div className="flex flex-wrap gap-2 mb-6">
        <select
          value={filterKind}
          onChange={(e) => setFilterKind(e.target.value)}
          data-testid="filter-kind"
          className="bg-obsidian border border-seam rounded-md px-3 py-2 text-sm text-textmain focus:outline-none focus:border-champagne"
        >
          <option value="">All kinds</option>
          {(meta?.task_kinds || []).map((k) => (
            <option key={k} value={k}>{KIND_LABELS[k] || k}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="filter-status"
          className="bg-obsidian border border-seam rounded-md px-3 py-2 text-sm text-textmain focus:outline-none focus:border-champagne"
        >
          <option value="">All statuses</option>
          {(meta?.task_statuses || []).map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 text-champagne animate-spin" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="regal-card p-16 text-center">
          <ListChecks className="w-8 h-8 text-champagne mx-auto mb-4" strokeWidth={1.4} />
          <h3 className="font-heading text-2xl mb-2">Nothing on the docket</h3>
          <p className="text-textmuted mb-5">Create the first task or delegation to begin.</p>
          {canCreate && (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="inline-flex items-center gap-2 bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-5 py-2.5"
            >
              <Plus className="w-4 h-4" strokeWidth={1.7} />
              New
            </button>
          )}
        </div>
      ) : (
        <div className="regal-card overflow-hidden">
          <div className="divide-y divide-seam">
            {tasks.map((t) => {
              const ent = t.entity_id ? entityById[t.entity_id] : null;
              const assignee = t.assigned_to ? adminByUserId[t.assigned_to] : null;
              return (
                <div
                  key={t.id}
                  data-testid={`task-row-${t.id}`}
                  className="px-5 py-4 hover:bg-elevated/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] tracking-wider uppercase text-champagne">
                          {KIND_LABELS[t.kind] || t.kind}
                        </span>
                        <span className={`text-[10px] uppercase tracking-wider ${PRIORITY_STYLES[t.priority] || ""}`}>
                          · {t.priority}
                        </span>
                        {t.approval_required && (
                          <span className="text-[10px] tracking-wider uppercase text-[#E5B493] border border-[#7a4a30]/50 bg-[#7a4a30]/20 rounded-full px-2 py-0.5">
                            Approval
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading text-xl text-textmain mt-1">{t.title}</h3>
                      {t.instruction && (
                        <p className="text-sm text-textmuted mt-1 leading-relaxed line-clamp-2">
                          {t.instruction}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-[11px] text-textmuted mt-2 flex-wrap">
                        {ent && <span>{ent.name}</span>}
                        {t.deadline && <span>Due {t.deadline.slice(0, 10)}</span>}
                        {t.follow_up_date && <span>Follow-up {t.follow_up_date.slice(0, 10)}</span>}
                        <span>
                          Assigned to: {assignee ? assignee.name || assignee.email : "Unassigned"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${
                          STATUS_STYLES[t.status] || STATUS_STYLES.new
                        }`}
                      >
                        {t.status.replace(/_/g, " ")}
                      </span>
                      {canEdit && (
                        <select
                          value={t.status}
                          onChange={(e) => updateStatus(t, e.target.value)}
                          data-testid={`task-status-select-${t.id}`}
                          className="bg-obsidian border border-seam rounded-md px-2 py-1 text-xs text-textmuted focus:outline-none focus:border-champagne"
                        >
                          {(meta?.task_statuses || []).map((s) => (
                            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <NewTaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={() => load()}
        meta={meta}
        entities={entities}
        admins={admins}
      />
    </Layout>
  );
};

export default EnterpriseTasks;
